/**
 * Client-side in-memory cache for RBAC roles (GET /api/v1/rbac/roles).
 * - Populated on first request or on login.
 * - Cleared on logout.
 * - Refreshed on every successful login so cache is up to date.
 */

import type { RoleWithPermissionsDto } from '../api/rbac';
import { getRolesWithPermissions } from '../api/rbac';

let cachedRoles: RoleWithPermissionsDto[] | null = null;

/**
 * Get cached roles if present.
 */
export function getCachedRoles(): RoleWithPermissionsDto[] | null {
  return cachedRoles;
}

/**
 * Set cache (used after fetch or on login refresh).
 */
export function setRolesCache(roles: RoleWithPermissionsDto[]): void {
  cachedRoles = roles;
}

/**
 * Clear the roles cache. Call on logout.
 */
export function clearRolesCache(): void {
  cachedRoles = null;
}

/**
 * Get roles: return cache if present, otherwise fetch from API, cache, and return.
 */
export async function getRoles(): Promise<RoleWithPermissionsDto[]> {
  if (cachedRoles != null) {
    return cachedRoles;
  }
  const roles = await getRolesWithPermissions();
  setRolesCache(roles);
  return roles;
}

/**
 * Refresh cache from API (e.g. on login). Replaces existing cache.
 */
export async function refreshRolesCache(): Promise<RoleWithPermissionsDto[]> {
  const roles = await getRolesWithPermissions();
  setRolesCache(roles);
  return roles;
}
